import React, { useState, FC, MouseEvent } from 'react'
import { Panel, PanelGroup } from 'rsuite'
import useTranslation from 'next-translate/useTranslation'

import { Product, ProductAttribute } from '../../../utils/types/Product'
import StandardTest from './StandardTest'
import { getProductBaseStandards, getProductLabels, getProductStandardTests, getProductStandardVersions } from '../../../lib/product'
import AttributeTitle from './AttributeTitle'
import contactEvent from '../../../utils/analytics/contactEvent'

import styles from '../../../styles/AttributeList.module.less'

interface AttributeListProps {
  product: Product
}

const StandardsAndLabels: FC<AttributeListProps> = (props) => {
  const { product } = props

  const { t } = useTranslation('products')
  const standardsBase = getProductBaseStandards(product)
  const standardsVersion = getProductStandardVersions(product)
  const standardsTest = getProductStandardTests(product)
  const labels = getProductLabels(product)

  const [activeTab, setActiveTab] = useState(1)

  const onActivatedTab = (tabId: number) => setActiveTab(
    (prevTab) => (tabId === prevTab ? 0 : tabId),
  )

  const openChat = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    if (!window.chaport) return
    window.chaport.q('open')
    // ANALYTICS-EVENT: Contact event
    contactEvent('product-standards', 'single_product_standards_chat')
  }

  const renderAttributesList = (
    attributeData: ProductAttribute[],
    startingIndex: number,
    renderLabelsList = false,
  ) => attributeData.map(
    (
      attribute,
      index,
    ) => {
      const attributeDescription = attribute?.['text.long']
      const listIndex = index + startingIndex

      // Include highlighted standards
      const filteredStandardTests = standardsTest?.filter(
        (stdTest) => stdTest['attribute.code'].includes(attribute['attribute.code']),
      )
        .map((stdTest, eventIndex) => (stdTest?.['text.long'] ? (
          <Panel
            key={stdTest.id}
            header={<StandardTest standardTest={stdTest} />}
            eventKey={eventIndex}
          >
            <p className={styles['product-attribute-description']}>
              {stdTest['text.long']}
            </p>
          </Panel>

        )
          : (
            <StandardTest
              key={stdTest['attribute.code']}
              standardTest={stdTest}
            />
          )))

      // Panel is expandable if it has a description or standard tests
      const isExpandable = !!(renderLabelsList && attributeDescription)
      || !!filteredStandardTests?.length

      // Keep the attribute on opened state (minus icon) if text doesn't exist
      const currentTabId = isExpandable ? activeTab : 0
      const eventKey = isExpandable ? listIndex : 0
      const headerText = attribute['attribute.label']

      const renderStandardTests = filteredStandardTests?.length
        ? (
          <div className={styles['standard-tests-wrapper']}>
            <PanelGroup
              accordion
              defaultActiveKey={0}
            >
              {filteredStandardTests}
            </PanelGroup>
          </div>
        )
        : (
          <button
            className={styles['no-standard-tests']}
            onClick={openChat}
            type="button"
          >
            {t('Reach out to ask about the standard test results')}
          </button>
        )

      return (
        <Panel
          as="li"
          key={attribute['attribute.id']}
          expanded={isExpandable ? currentTabId === eventKey : false}
          bordered={false}
          header={(
            <AttributeTitle
              eventKey={eventKey}
              tabId={currentTabId}
              isExpandable={isExpandable}
              text={headerText}
            />
          )}
          eventKey={listIndex}
        >
          <p className="product-attribute-description">{attributeDescription}</p>
          {!!filteredStandardTests?.length && <h6 className={styles['test-heading']}>{t('Test results')}</h6>}
          {!renderLabelsList && renderStandardTests}
        </Panel>
      )
    },
  )

  const renderStandardsAndLabels = () => {
    // Use base standard if version isn't defined
    const standards = standardsBase?.map((base) => {
      const version = standardsVersion?.find((ver) => ver['attribute.code'].includes(base['attribute.code']))
      if (version) return version
      return base
    })

    if (!standards || !labels) return null

    const renderedStandards = standards.length
      ? renderAttributesList(standards, 1)
      : <p>{t('Standards have not been specified')}</p>

    const renderedLabels = renderAttributesList(labels, standards.length + 1, true)

    return (
      <>
        {renderedStandards}
        {labels.length > 0 && (
        <>
          {renderedLabels}
        </>
        )}
      </>
    )
  }

  return (
    <PanelGroup
      as="ul"
      className={styles['product-attribute-list']}
      accordion
      bordered={false}
      defaultActiveKey={1}
      onSelect={(tabId) => onActivatedTab(Number(tabId))}
    >
      {renderStandardsAndLabels()}
    </PanelGroup>
  )
}

export default StandardsAndLabels
